"use client";

import { useMemo, useState } from "react";
import { Frame, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Essayez un motif sur du texte",
        pattern: "Motif",
        text: "Texte cherché",
        examples: "Exemples",
        matches: "correspondance",
        matchesPlural: "correspondances",
        none: "Aucune correspondance. Le motif est valide, il ne trouve simplement rien dans ce texte.",
        found: "Les parties surlignées sont ce que findall renverrait. Les groupes entre parenthèses sont extraits séparément, ce qui sert à récupérer une partie de ce qu'on a trouvé.",
        invalid: "Motif invalide : Python lèverait une erreur de compilation du motif avant même de chercher.",
        groups: "Groupes capturés",
        note: "Le moteur utilisé ici est celui du navigateur, très proche de celui de Python sur les motifs courants mais pas identique. Python nomme ses groupes (?P<nom>...) là où le navigateur écrit (?<nom>...), et le module re propose des drapeaux que cette démonstration n'expose pas.",
        ex1: "les nombres",
        ex2: "un e-mail",
        ex3: "une date",
        ex4: "un mot en majuscules",
    },
    en: {
        title: "Try a pattern on some text",
        pattern: "Pattern",
        text: "Text searched",
        examples: "Examples",
        matches: "match",
        matchesPlural: "matches",
        none: "No match. The pattern is valid, it simply finds nothing in this text.",
        found: "The highlighted parts are what findall would hand back. Groups in brackets are extracted separately, which is how you pull out part of what you found.",
        invalid: "Invalid pattern: Python would raise a compilation error before searching at all.",
        groups: "Captured groups",
        note: "The engine here is the browser's, very close to Python's on common patterns but not identical. Python names its groups (?P<name>...) where the browser writes (?<name>...), and the re module offers flags this demonstration does not expose.",
        ex1: "numbers",
        ex2: "an email",
        ex3: "a date",
        ex4: "an uppercase word",
    },
};

const TEXTE = {
    fr: "Facture 2026-08-19 : 3 articles, total 149,90 EUR. Contact : marie@believemy.com, commande REF-4471 du 12/06/2026.",
    en: "Invoice 2026-08-19: 3 items, total 149.90 EUR. Contact: marie@believemy.com, order REF-4471 from 12/06/2026.",
};

const EXEMPLES = [
    { cle: "ex1", motif: "\\d+" },
    { cle: "ex2", motif: "[\\w.]+@[\\w.]+" },
    { cle: "ex3", motif: "(\\d{4})-(\\d{2})-(\\d{2})" },
    { cle: "ex4", motif: "[A-Z]{2,}" },
];

export default function RegexTester({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const texte = TEXTE[lang] || TEXTE.fr;
    const [motif, setMotif] = useState("\\d+");

    const { trouve, morceaux, groupes } = useMemo(() => {
        if (!motif) return { trouve: null, morceaux: [{ txt: texte }], groupes: [] };
        let re;
        try {
            // Le motif est plafonné : un texte court et un motif court écartent
            // les explosions combinatoires sur une page publique.
            re = new RegExp(motif.slice(0, 80), "g");
        } catch {
            return { trouve: false, morceaux: [{ txt: texte }], groupes: [] };
        }
        const out = [];
        const grp = [];
        let dernier = 0;
        let n = 0;
        for (const m of texte.matchAll(re)) {
            if (m[0] === "" || n > 40) break;
            out.push({ txt: texte.slice(dernier, m.index) });
            out.push({ txt: m[0], hit: true });
            if (m.length > 1) grp.push(m.slice(1));
            dernier = m.index + m[0].length;
            n++;
        }
        out.push({ txt: texte.slice(dernier) });
        return { trouve: n, morceaux: out, groupes: grp };
    }, [motif, texte]);

    return (
        <Frame icon="magnifer-linear" title={t.title}>
            <label className="text-sm text-gray-600 dark:text-lightBlue mb-1.5 block">{t.pattern}</label>
            <input
                type="text"
                value={motif}
                maxLength={80}
                onChange={(e) => setMotif(e.target.value)}
                spellCheck={false}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-geistMono text-sm text-gray-900 focus:border-primary focus:outline-hidden dark:border-white/10 dark:bg-white/[0.04] dark:text-white"
            />

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-400 dark:text-grayDark-500">{t.examples} :</span>
                {EXEMPLES.map((e) => (
                    <button
                        key={e.cle}
                        onClick={() => setMotif(e.motif)}
                        className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600 transition-colors duration-200 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-grayDark-300 dark:hover:bg-white/10"
                    >
                        {t[e.cle]}
                    </button>
                ))}
            </div>

            <div className="mt-5 rounded-xl bg-gray-50 px-4 py-3 font-geistMono text-[13px] leading-relaxed dark:bg-white/[0.04] dark:text-lightBlue">
                {morceaux.map((m, i) =>
                    m.hit ? (
                        <mark key={i} className="rounded bg-primary/20 px-0.5 text-primary dark:bg-primary/25">
                            {m.txt}
                        </mark>
                    ) : (
                        <span key={i}>{m.txt}</span>
                    ),
                )}
            </div>

            {groupes.length > 0 && (
                <div className="mt-3 text-sm text-gray-600 dark:text-lightBlue">
                    <span className="text-xs uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                        {t.groups}
                    </span>
                    <div className="mt-1 font-geistMono text-[13px] dark:text-white">
                        {groupes.map((g, i) => (
                            <div key={i}>({g.map((x) => `'${x}'`).join(", ")})</div>
                        ))}
                    </div>
                </div>
            )}

            <Verdict tone={trouve === false ? "bad" : trouve === 0 ? "warn" : "good"}>
                {trouve === false
                    ? t.invalid
                    : trouve === 0
                      ? t.none
                      : `${trouve} ${trouve > 1 ? t.matchesPlural : t.matches}. ${t.found}`}
            </Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
